import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Chip,
  Avatar,
    Drawer,
  Divider,
  Tooltip,
  Pagination,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Send,
  SmartToy,
  Person,
  Add,
  Delete,
  MenuOpen,
  Chat,
  Lock,
  Close,
  ArrowBack,
  Stop,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import colors from '../design-system/colors';
import spacing from '../design-system/spacing';
import shadows from '../design-system/shadows';
import typography from '../design-system/typography';
import { Card, CardContent, Button } from '../design-system/components';
import { ContentBlockRouter } from '../components/chat';
import {
  sendChatMessageStream,
  getConversations,
  getConversation,
  deleteConversation,
} from '../services/chatAgentService';
import usePageTitle from '../hooks/usePageTitle';
import { useAuth } from '../context/AuthContext';

const SIDEBAR_WIDTH = 280;

const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/* ─────────── Typing Indicator ─────────── */
const typingKeyframes = `
@keyframes typingBounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-6px); opacity: 1; }
}`;

const TypingIndicator = () => (
  <>
    <style>{typingKeyframes}</style>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', p: 1, minHeight: 24 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: colors.primary[500],
            animation: `typingBounce 1.2s ease-in-out ${i * 0.15}s infinite`,
          }}
        />
      ))}
    </Box>
  </>
);

/* ─────────── Premium Gate ─────────── */
const PremiumGate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('chat');
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 'calc(100vh - 64px)',
        backgroundColor: 'background.default',
        p: 3,
      }}
    >
      <Card sx={{ maxWidth: 440, textAlign: 'center' }}>
        <CardContent sx={{ p: 4 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              mx: 'auto',
              mb: 2,
              backgroundColor: colors.secondary[500],
            }}
          >
            <Lock sx={{ fontSize: 32, color: colors.neutral.white }} />
          </Avatar>
          <Typography variant="h5" sx={{ fontWeight: typography.fontWeight.bold, mb: 1 }}>
            {t('premium.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            {t('premium.description')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/dashboard')}
            startIcon={<ArrowBack />}
            sx={{
              mb: 2,
              backgroundColor: colors.primary[500],
              '&:hover': { backgroundColor: colors.primary[600] },
            }}
          >
            {t('premium.upgrade')}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

/* ─────────── Sidebar ─────────── */
const ConversationSidebar = ({
  open,
  onClose,
  conversations,
  activeId,
  onSelect,
  onDelete,
  onNew,
  isMobile,
  page,
  totalPages,
  onPageChange,
}) => {
  const { t } = useTranslation('chat');
  const content = (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper',
        borderRight: `1px solid ${colors.divider}`,
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          onClick={onNew}
          sx={{
            flex: 1,
            justifyContent: 'flex-start',
            gap: 1,
            textTransform: 'none',
            backgroundColor: colors.primary[500],
            color: colors.neutral.white,
            '&:hover': { backgroundColor: colors.primary[600] },
          }}
        >
          <Add fontSize="small" /> {t('sidebar.newConversation')}
        </Button>
        {isMobile && (
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {conversations.length === 0 && (
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mt: 4 }}
          >
            {t('sidebar.noConversations')}
          </Typography>
        )}
        {conversations.map((conv) => (
          <Box
            key={conv.id}
            onClick={() => onSelect(conv.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.2,
              cursor: 'pointer',
              backgroundColor: conv.id === activeId ? 'action.selected' : 'transparent',
              borderLeft: conv.id === activeId ? `3px solid ${colors.primary[500]}` : '3px solid transparent',
              transition: 'all 0.15s ease',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <Chat sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontWeight: conv.id === activeId ? typography.fontWeight.semibold : typography.fontWeight.regular,
                  color: 'text.primary',
                  fontSize: typography.fontSize.sm,
                }}
              >
                {conv.title || t('sidebar.conversation')}
              </Typography>
              {conv.updatedAt && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                  {new Date(conv.updatedAt).toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: 'short',
                  })}
                </Typography>
              )}
            </Box>
            <Tooltip title={t('sidebar.delete')}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(conv.id);
                }}
                sx={{
                  opacity: 0.4,
                  '&:hover': { opacity: 1, color: colors.error.main },
                }}
              >
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Box>

      {totalPages > 1 && (
        <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_event, value) => onPageChange(value)}
            size="small"
            color="primary"
            shape="rounded"
            siblingCount={0}
            boundaryCount={1}
          />
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer anchor="left" open={open} onClose={onClose}>
        {content}
      </Drawer>
    );
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: SIDEBAR_WIDTH, opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{ overflow: 'hidden', flexShrink: 0 }}
        >
          {content}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ─────────── Welcome Empty State ─────────── */
const WelcomeState = ({ onPromptClick, quickPrompts }) => {
  const { t } = useTranslation('chat');
  return (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      textAlign: 'center',
      px: 3,
      py: 6,
    }}
  >
    <Avatar
      sx={{
        width: 56,
        height: 56,
        mb: 2,
        backgroundColor: colors.primary[100],
      }}
    >
      <SmartToy sx={{ fontSize: 30, color: colors.primary[600] }} />
    </Avatar>
    <Typography variant="h6" sx={{ fontWeight: typography.fontWeight.bold, color: 'text.primary', mb: 0.5 }}>
      {t('header.title')}
    </Typography>
    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, maxWidth: 420 }}>
      {t('messages.welcomeDesc')}
    </Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', maxWidth: 500 }}>
      {quickPrompts.map((prompt) => (
        <Chip
          key={prompt}
          label={prompt}
          onClick={() => onPromptClick(prompt)}
          sx={{
            backgroundColor: 'background.paper',
            border: `1px solid ${colors.primary[200]}`,
            color: colors.primary[700],
            fontWeight: typography.fontWeight.medium,
            fontSize: typography.fontSize.xs,
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: colors.primary[50],
              borderColor: colors.primary[400],
              boxShadow: shadows.sm,
            },
          }}
        />
      ))}
    </Box>
  </Box>
  );
};

/* ─────────── Message Bubble ─────────── */
const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginBottom: 12,
        paddingLeft: isUser ? 48 : 0,
        paddingRight: isUser ? 0 : 48,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, maxWidth: '100%', alignItems: 'flex-start' }}>
        {!isUser && (
          <Avatar
            sx={{
              width: 30,
              height: 30,
              backgroundColor: colors.primary[100],
              mt: 0.5,
              flexShrink: 0,
            }}
          >
            <SmartToy sx={{ fontSize: 18, color: colors.primary[600] }} />
          </Avatar>
        )}

        <Box
          sx={{
            backgroundColor: isUser ? colors.primary[500] : 'background.paper',
            color: isUser ? colors.neutral.white : 'text.primary',
            borderRadius: isUser
              ? `${spacing.borderRadius.lg}px ${spacing.borderRadius.lg}px ${spacing.borderRadius.sm}px ${spacing.borderRadius.lg}px`
              : `${spacing.borderRadius.lg}px ${spacing.borderRadius.lg}px ${spacing.borderRadius.lg}px ${spacing.borderRadius.sm}px`,
            px: 2,
            py: 1.5,
            boxShadow: shadows.sm,
            maxWidth: '100%',
            minWidth: 0,
            overflow: 'hidden',
            border: isUser ? 'none' : '1px solid',
            borderColor: isUser ? 'transparent' : 'divider',
          }}
        >
          {isUser ? (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {typeof message.content === 'string'
                ? message.content
                : message.content?.[0]?.content || ''}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {Array.isArray(message.content) ? (
                message.content.map((block, i) => (
                  <ContentBlockRouter key={i} block={block} />
                ))
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {typeof message.content === 'string' ? message.content : JSON.stringify(message.content)}
                </Typography>
              )}
            </Box>
          )}

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'right',
              mt: 0.5,
              fontSize: '0.6rem',
              opacity: 0.6,
              color: isUser ? colors.neutral.white : colors.text.hint,
            }}
          >
            {new Date(message.timestamp).toLocaleTimeString('es-ES', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
        </Box>

        {isUser && (
          <Avatar
            sx={{
              width: 30,
              height: 30,
              backgroundColor: colors.primary[700],
              mt: 0.5,
              flexShrink: 0,
            }}
          >
            <Person sx={{ fontSize: 18, color: colors.neutral.white }} />
          </Avatar>
        )}
      </Box>
    </motion.div>
  );
};

/* ─────────── Main Page ─────────── */
const ChatAgentPageModern = () => {
  usePageTitle('Chat Asistente');
  const { t } = useTranslation('chat');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const QUICK_PROMPTS = [
    t('quickPrompts.topScorers'),
    t('quickPrompts.teamStreak'),
    t('quickPrompts.compareTeams'),
    t('quickPrompts.mostCards'),
    t('quickPrompts.homeAway'),
    t('quickPrompts.cleanSheets'),
  ];

  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [conversations, setConversations] = useState([]);
  const [conversationsPage, setConversationsPage] = useState(1);
  const [conversationTotalPages, setConversationTotalPages] = useState(0);
  const [conversationTotal, setConversationTotal] = useState(0);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const streamRef = useRef(null);

  const { hasPermission } = useAuth();
  const isPremium = hasPermission('analytics.view');

  /* ── Scroll to bottom ── */
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, isStreaming, streamingText, scrollToBottom]);

  const loadConversations = useCallback(
    async (nextPage = conversationsPage) => {
      try {
        const result = await getConversations({ page: nextPage, limit: 10 }, navigate);
        setConversations(Array.isArray(result.data) ? result.data : []);
        setConversationTotal(result.total || 0);
        setConversationTotalPages(result.totalPages || 0);
        setConversationsPage(result.page || nextPage);
      } catch {
        /* silently fail for sidebar */
      }
    },
    [conversationsPage, navigate]
  );

  /* ── Load conversations on mount ── */
  useEffect(() => {
    if (!isPremium) return;
    loadConversations(conversationsPage);
  }, [conversationsPage, isPremium, loadConversations]);

  /* ── Stop streaming ── */
  const handleStop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (streamRef.current?.cancel) {
      streamRef.current.cancel();
      streamRef.current = null;
    }
  }, []);

  /* ── Send message (streaming) ── */
  const handleSend = useCallback(
    async (text) => {
      const trimmed = (text || inputValue).trim();
      if (!trimmed || isLoading || isStreaming) return;

      setInputValue('');
      setError(null);

      const userMsg = {
        id: generateId(),
        role: 'user',
        content: trimmed,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);
      setStreamingText('');

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const stream = await sendChatMessageStream(
          trimmed, conversationId, navigate, abortController.signal
        );
        streamRef.current = stream;
        setIsStreaming(true);

        let accumulatedText = '';
        let stopped = false;
        let receivedFirstDelta = false;

        for await (const event of stream) {
          if (abortController.signal.aborted) {
            stopped = true;
            break;
          }

          if (event.type === 'meta' && event.conversationId) {
            setConversationId(event.conversationId);
          }

          if (event.type === 'delta' && event.text) {
            if (!receivedFirstDelta) {
              receivedFirstDelta = true;
              setIsLoading(false);
            }
            accumulatedText += event.text;
            setStreamingText(accumulatedText);
          }

          if (event.type === 'done') {
            const blocks = event.content || [{ type: 'text', value: accumulatedText || 'Sin respuesta.' }];
            const assistantMsg = {
              id: generateId(),
              role: 'assistant',
              content: Array.isArray(blocks) ? blocks : [{ type: 'text', value: String(blocks) }],
              timestamp: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, assistantMsg]);
            setStreamingText('');
          }

          if (event.type === 'error') {
            throw new Error(event.message || 'Error del agente');
          }
        }

        // If user stopped mid-stream, save partial text
        if (stopped && accumulatedText) {
          const partialMsg = {
            id: generateId(),
            role: 'assistant',
            content: [{ type: 'text', value: accumulatedText + '\n\n*⏹ Respuesta detenida*' }],
            timestamp: new Date().toISOString(),
          };
          setMessages((prev) => [...prev, partialMsg]);
          setStreamingText('');
        }

        setIsStreaming(false);

        // Refresh sidebar
        try {
          await loadConversations(conversationsPage);
        } catch {
          /* ok */
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          setIsStreaming(false);
          setIsLoading(false);
          return;
        }
        setError(err.message);
        setStreamingText('');
        setIsStreaming(false);
        const errorMsg = {
          id: generateId(),
          role: 'assistant',
          content: [
            {
              type: 'error',
              message: `Error de conexión: ${err.message}. Intenta de nuevo.`,
            },
          ],
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setIsLoading(false);
        setIsStreaming(false);
        abortControllerRef.current = null;
        streamRef.current = null;
        inputRef.current?.focus();
      }
    },
    [conversationId, conversationsPage, inputValue, isLoading, isStreaming, loadConversations, navigate]
  );

  /* ── Key handler ── */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /* ── Load conversation ── */
  const handleSelectConversation = useCallback(
    async (id) => {
      try {
        setIsLoading(true);
        const data = await getConversation(id, navigate);
        setConversationId(id);
        const loaded = (data.messages || []).map((m) => ({
          id: m.id || generateId(),
          role: m.role,
          content: m.content,
          timestamp: m.timestamp || new Date().toISOString(),
        }));
        setMessages(loaded);
        if (isMobile) setSidebarOpen(false);
      } catch {
        setError('No se pudo cargar la conversación.');
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, isMobile]
  );

  /* ── Delete conversation ── */
  const handleDeleteConversation = useCallback(
    async (id) => {
      try {
        await deleteConversation(id, navigate);
        if (conversationId === id) {
          setConversationId(null);
          setMessages([]);
        }

        const nextPage = conversations.length === 1 && conversationsPage > 1
          ? conversationsPage - 1
          : conversationsPage;

        await loadConversations(nextPage);
      } catch {
        /* ok */
      }
    },
    [conversationId, conversations.length, conversationsPage, loadConversations, navigate]
  );

  /* ── New conversation ── */
  const handleNewConversation = () => {
    setConversationId(null);
    setMessages([]);
    setError(null);
    setInputValue('');
    if (isMobile) setSidebarOpen(false);
  };

  /* ── Premium check ── */
  if (!isPremium) return <PremiumGate />;

  return (
    <Box
      className="wp-page wp-chat"
      sx={{
        position: 'fixed',
        top: { xs: 56, sm: 64 },
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        overflow: 'hidden',
        backgroundColor: 'background.default',
        zIndex: 5,
      }}
    >
      {/* Sidebar */}
      <ConversationSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        conversations={conversations}
        activeId={conversationId}
        onSelect={handleSelectConversation}
        onDelete={handleDeleteConversation}
        onNew={handleNewConversation}
        isMobile={isMobile}
        page={conversationsPage}
        totalPages={conversationTotalPages}
        onPageChange={setConversationsPage}
      />

      {/* Main chat area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%', overflow: 'hidden' }}>
        {/* Header */}
        <Box
          className="wp-chat-heading"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            backgroundColor: 'background.paper',
            borderBottom: 1, borderColor: 'divider',
            boxShadow: shadows.xs,
            flexShrink: 0,
          }}
        >
          <Tooltip title={t('header.back')}>
            <IconButton
              size="small"
              onClick={() => navigate('/dashboard')}
              sx={{ color: colors.primary[600] }}
            >
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <Tooltip title={sidebarOpen ? t('header.hideHistory') : t('header.showHistory')}>
            <IconButton
              size="small"
              onClick={() => setSidebarOpen((prev) => !prev)}
              sx={{ color: 'text.secondary' }}
            >
              <MenuOpen
                sx={{
                  transform: sidebarOpen ? 'scaleX(1)' : 'scaleX(-1)',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          </Tooltip>
          <Avatar sx={{ width: 32, height: 32, backgroundColor: colors.primary[500] }}>
            <SmartToy sx={{ fontSize: 20, color: colors.neutral.white }} />
          </Avatar>
          <Box>
            <Typography component="h1" variant="subtitle1" sx={{ fontWeight: typography.fontWeight.bold, lineHeight: 1.2 }}>
              {t('header.title')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
              {t('header.subtitle')} · {conversationTotal} conversaciones
            </Typography>
          </Box>
        </Box>

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            px: { xs: 2, md: 3 },
            py: 2,
          }}
        >
          {messages.length === 0 && !isLoading ? (
            <WelcomeState onPromptClick={(prompt) => handleSend(prompt)} quickPrompts={QUICK_PROMPTS} />
          ) : (
            <>
              {messages.map((msg) => (
                <MessageBubble key={msg.id} message={msg} />
              ))}
              {isStreaming && streamingText && (
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'flex-start' }}>
                  <Avatar
                    sx={{
                      width: 30,
                      height: 30,
                      backgroundColor: colors.primary[100],
                      mt: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    <SmartToy sx={{ fontSize: 18, color: colors.primary[600] }} />
                  </Avatar>
                  <Box
                    sx={{
                      backgroundColor: 'background.paper',
                      borderRadius: `${spacing.borderRadius.lg}px ${spacing.borderRadius.lg}px ${spacing.borderRadius.lg}px ${spacing.borderRadius.sm}px`,
                      px: 2,
                      py: 1.5,
                      boxShadow: shadows.sm,
                      maxWidth: '100%',
                      minWidth: 0,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <ContentBlockRouter block={{ type: 'text', value: streamingText }} />
                  </Box>
                </Box>
              )}
              {(isLoading || (isStreaming && !streamingText)) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Avatar
                    sx={{
                      width: 30,
                      height: 30,
                      backgroundColor: colors.primary[100],
                    }}
                  >
                    <SmartToy sx={{ fontSize: 18, color: colors.primary[600] }} />
                  </Avatar>
                  <Box
                    sx={{
                      backgroundColor: 'background.paper',
                      borderRadius: `${spacing.borderRadius.lg}px`,
                      px: 2,
                      py: 1,
                      boxShadow: shadows.sm,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <TypingIndicator />
                  </Box>
                </Box>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </Box>

          {messages.length > 0 && messages.length <= 2 && !isLoading && (
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              pb: 1,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.8,
            }}
          >
            {QUICK_PROMPTS.slice(0, 4).map((prompt) => (
              <Chip
                key={prompt}
                label={prompt}
                size="small"
                onClick={() => handleSend(prompt)}
                sx={{
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderColor: 'primary.main',
                  },
                }}
              />
            ))}
          </Box>
        )}

        {/* Error banner */}
        {error && (
          <Box
            sx={{
              mx: { xs: 2, md: 3 },
              mb: 1,
              p: 1,
              borderRadius: `${spacing.borderRadius.md}px`,
              backgroundColor: colors.error[50],
              border: `1px solid ${colors.error[200]}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="caption" sx={{ color: colors.error[700] }}>
              {error}
            </Typography>
            <Button
              size="small"
              onClick={() => setError(null)}
              sx={{ minWidth: 'auto', fontSize: '0.7rem', color: colors.error[700] }}
            >
              {t('errors.close')}
            </Button>
          </Box>
        )}

        {/* Input bar */}
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            backgroundColor: 'background.paper',
            borderTop: 1, borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-end',
              backgroundColor: 'background.default',
              borderRadius: `${spacing.borderRadius.xl}px`,
              px: 2,
              py: 0.5,
              border: `1px solid ${colors.neutral[300]}`,
              transition: 'border-color 0.2s',
              '&:focus-within': {
                borderColor: colors.primary[400],
                boxShadow: shadows.focus,
              },
            }}
          >
            <TextField
              inputRef={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('input.placeholder')}
              multiline
              maxRows={4}
              disabled={isLoading || isStreaming}
              fullWidth
              variant="standard"
              slotProps={{
                input: {
                  disableUnderline: true,
                  sx: {
                    fontSize: typography.fontSize.sm,
                    py: 1,
                  },
                },
              }}
            />
            {(isLoading || isStreaming) ? (
              <IconButton
                onClick={handleStop}
                sx={{
                  backgroundColor: colors.error.main,
                  color: colors.neutral.white,
                  width: 36,
                  height: 36,
                  mb: 0.5,
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: colors.error[700] || '#c62828',
                  },
                }}
              >
                <Stop sx={{ fontSize: 20 }} />
              </IconButton>
            ) : (
              <IconButton
                onClick={() => handleSend()}
                disabled={!inputValue.trim()}
                sx={{
                  backgroundColor: inputValue.trim() ? colors.primary[500] : 'action.disabledBackground',
                  color: colors.neutral.white,
                  width: 36,
                  height: 36,
                  mb: 0.5,
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: colors.primary[600],
                  },
                  '&.Mui-disabled': {
                    backgroundColor: colors.neutral[300],
                    color: colors.neutral.white,
                  },
                }}
              >
                <Send sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 0.8,
              color: 'text.secondary',
              fontSize: '0.6rem',
            }}
          >
            El agente puede cometer errores. Verifica los datos importantes.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatAgentPageModern;
